import React, { useMemo } from 'react';
import logo from '../../assets/logo.svg';
import logoDark from '../../assets/logo-dark.svg';
import Menu from "../Menu/menu.js";
import {useDarkMode} from "../../utils/darkModeContext.js";
import { useSidebar } from "../../utils/sidebarContext.js";

const Sidebar = React.memo(() => {
    const {isDarkMode} = useDarkMode();
    const { isCollapsed, toggleSidebar } = useSidebar();

    const DoubleChevronLeft = useMemo(() => (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
            <path d="M9 11L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11L9 8L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    ), []);

    const DoubleChevronRight = useMemo(() => (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
            <path d="M7 5L10 8L7 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4 5L7 8L4 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    ), []);

    return (
        <div className="hidden custom-860:block relative h-full" style={{ overflow: 'visible' }}>
            <aside 
                className={`flex bg-white dark:bg-[#161b22] border-r border-gray-200 dark:border-[#30363d] flex-col h-full relative ${
                    isCollapsed ? 'w-16' : 'w-52'
                }`}
                style={{
                    transition: 'width 120ms cubic-bezier(0.4, 0, 0.2, 1)',
                    contain: 'style paint',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    willChange: 'width',
                    zIndex: 10
                }}
            >
                <div className={`p-3 flex items-center flex-shrink-0 relative ${isCollapsed ? 'justify-center flex-col gap-2' : 'justify-between pl-6 pr-6'}`}>
                    {/* Favicon - shown when collapsed */}
                    <div 
                        className={`flex items-center justify-center w-full transition-opacity duration-120 ${isCollapsed ? 'opacity-100' : 'opacity-0 absolute pointer-events-none'}`}
                        style={{ transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)' }}
                    >
                        <img 
                            src="/favicon.svg" 
                            alt="byCHEF Logo" 
                            className="object-contain"
                            style={{ width: '46px', height: '46px' }}
                            onError={(e) => {
                                console.error("Failed to load favicon:", "/favicon.svg");
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                    {/* Logo - shown when expanded */}
                    <img 
                        src={isDarkMode ? logoDark : logo} 
                        alt="byCHEF Logo" 
                        className={`w-[121px] h-[46px] transition-opacity duration-120 ${isCollapsed ? 'opacity-0 absolute pointer-events-none' : 'opacity-100'}`}
                        style={{ transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)' }}
                        onError={(e) => {
                            console.error("Failed to load logo:", isDarkMode ? logoDark : logo);
                            e.target.onerror = null;
                            e.target.src = "/favicon.svg";
                        }}
                    />
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                    <Menu />
                </div>
            </aside>
            <button
                onClick={toggleSidebar}
                className="absolute top-1/2 -translate-y-1/2 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-full w-8 h-8 flex items-center justify-center shadow-md hover:shadow-lg transition-shadow duration-150"
                style={{ 
                    zIndex: 50,
                    pointerEvents: 'auto',
                    right: '-16px',
                    transition: 'box-shadow 150ms ease-in-out'
                }}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
                <div className="flex items-center justify-center text-gray-700 dark:text-gray-300" style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isCollapsed ? DoubleChevronRight : DoubleChevronLeft}
                </div>
            </button>
        </div>
    );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
